import type { SpotKind } from "../../shared/mission-quest.ts";
import type { QuestPageBlock } from "../../shared/quest-page-blocks.ts";
import type { RoutePlace } from "../../shared/place-label.ts";
import type { QuestType } from "../../shared/route-trace.ts";
import type { RouteFillFeature, RoutePaintFeature } from "../../shared/quest-route-paint.ts";
import { spotThumbnailKeyFromImageUrl } from "../../shared/spot-thumbnail.ts";
import type { LngLat, WorkArea } from "../../shared/types.ts";

export interface QuestRouteLine {
  coordinates: LngLat[];
  closed: boolean;
  length_m: number;
  /** Editor canvas the route was drawn in; its bearing turns the quest map. Null or missing: north-up. */
  canvas?: WorkArea | null;
  /** Styled segments copied when the route was saved; missing on older quests. */
  paint?: RoutePaintFeature[];
  /** Closed region fills copied when the route was saved; missing on older quests. */
  fill?: RouteFillFeature[];
}

export interface QuestSpot {
  seq?: number;
  lat: number;
  lng: number;
  radius_m: number;
  label: string | null;
  needs_media: boolean;
  title: string | null;
  est_minutes: number | null;
  hidden_story?: string | null;
  pose_hint?: string | null;
  link_url?: string | null;
  kind: SpotKind | null;
  /** Original language of this spot's text (BCP-47); follows the quest when blank. */
  source_lang?: string | null;
  /** Public URL when a thumbnail was uploaded for this spot. */
  thumbnail_image?: string | null;
  /** Gallery photos shown on the spot section (thumbnail is separate, for map markers). */
  images?: QuestSpotImage[];
}

export interface QuestSpotImage {
  image: string;
  caption?: string | null;
}

/** Normalize API/editor spot gallery entries (legacy string URLs included). */
export function normalizeQuestSpotImages(raw: unknown): QuestSpotImage[] {
  if (!Array.isArray(raw)) return [];
  const out: QuestSpotImage[] = [];
  for (const item of raw) {
    if (typeof item === "string" && item.trim()) {
      out.push({ image: item, caption: null });
      continue;
    }
    if (!item || typeof item !== "object") continue;
    const rec = item as Record<string, unknown>;
    const image = rec.image;
    if (typeof image !== "string" || !image.trim()) continue;
    const cap = rec.caption;
    out.push({
      image,
      caption: typeof cap === "string" ? cap : cap == null ? null : null,
    });
  }
  return out;
}

export interface QuestPagePhoto {
  image?: string | null;
  caption?: string | null;
}

export interface QuestDetail {
  id: string;
  category: string;
  preset: string | null;
  template_id: string | null;
  title: string;
  description: string;
  author: string | null;
  center_lat: number;
  center_lng: number;
  status: "draft" | "active" | "hidden" | "removed";
  cover_url: string | null;
  /** Uploaded cover photo (16:9; older ones are 4:3 and shown center-cropped). Null: the page shows the route map as its cover. */
  cover_image?: string | null;
  tagline: string | null;
  city: string | null;
  country: string | null;
  tags: string[];
  story: string | null;
  manners: string | null;
  quest_type?: QuestType | null;
  has_route?: boolean;
  route_settings_done?: boolean;
  /** The route's area name per UI language, looked up by the server; null until then. */
  route_place?: RoutePlace | null;
  start_fixed?: boolean;
  time_limit_s?: number | null;
  route_piece_id?: string | null;
  route?: QuestRouteLine | null;
  /** Original language of the creator's text (BCP-47), detected by the server on each save. */
  source_lang?: string;
  /** Always true: the server detects the language; clients can't set it. */
  source_lang_auto?: boolean;
  mine: boolean;
  /** Present when the viewer is signed in: whether they saved this quest. */
  saved?: boolean;
  can_edit: boolean;
  can_delete: boolean;
  created_at: string;
  /** When it was published; null for drafts (and quests published before this was kept). */
  published_at?: string | null;
  checkpoints: QuestSpot[];
  /** Inline photo blocks on the quest page (image URL + optional caption). */
  page_photos?: QuestPagePhoto[];
  /** Custom blocks (tip, quote, link, divider, quiz, gallery). */
  page_blocks?: QuestPageBlock[];
  /** Custom quest page section order from the editor; null uses legacy layout on the public page. */
  page_order?: string[] | null;
}

/** One published quest in the home map's nearby list (`GET /api/quests/nearby`). */
export interface NearbySpotPreview {
  lat: number;
  lng: number;
  kind: SpotKind | null;
  title?: string | null;
  thumbnail_image?: string | null;
}

export interface NearbyQuest extends Omit<QuestDetail, "checkpoints" | "route"> {
  start: { lat: number; lng: number };
  /** Meters from the map center to the quest's Start. */
  distance_m: number;
  /** A light copy of the route for the map; `length_m` is the full route's. */
  route_preview?: QuestRouteLine | null;
  /** Spot-only quests: pins for map framing (no route preview line). */
  spots_preview?: NearbySpotPreview[];
  spot_count?: number;
  est_minutes_total?: number;
}

export interface QuestWriteBody {
  category?: string;
  preset?: string | null;
  template_id?: string | null;
  title?: string;
  description?: string;
  cover_url?: string | null;
  tagline?: string | null;
  city?: string | null;
  country?: string | null;
  tags?: string[];
  story?: string | null;
  manners?: string | null;
  quest_type?: QuestType | null;
  start_fixed?: boolean;
  time_limit_s?: number | null;
  route_settings_done?: boolean;
  center_lat?: number;
  center_lng?: number;
  page_order?: string[] | null;
  page_photos?: Array<{ caption?: string | null }>;
  page_blocks?: QuestPageBlock[];
  spots?: Array<{
    lat: number;
    lng: number;
    radius_m?: number;
    title?: string | null;
    est_minutes?: number | null;
    needs_media?: boolean;
    hidden_story?: string | null;
    pose_hint?: string | null;
    link_url?: string | null;
    kind?: SpotKind | null;
    label?: string | null;
    thumbnail_key?: string | null;
    images?: Array<{ caption?: string | null }>;
  }>;
}

export function emptyQuestDraft(center?: { lat: number; lng: number }): QuestDetail {
  return {
    id: "",
    category: "todo",
    preset: null,
    template_id: null,
    title: "",
    description: "",
    author: null,
    center_lat: center?.lat ?? 37.5665,
    center_lng: center?.lng ?? 126.978,
    status: "draft",
    cover_url: null,
    tagline: null,
    city: null,
    country: null,
    tags: [],
    story: null,
    manners: null,
    mine: true,
    can_edit: true,
    can_delete: true,
    created_at: new Date().toISOString(),
    checkpoints: [],
    page_photos: [],
    page_blocks: [],
  };
}

function pageBlockWriteBody(block: QuestPageBlock): QuestPageBlock {
  if (block.kind === "gallery") {
    return { kind: "gallery", items: block.items.map((i) => ({ caption: i.caption ?? null })) };
  }
  return block;
}

export function toWriteBody(q: QuestDetail): QuestWriteBody {
  return {
    category: q.category || "todo",
    preset: q.template_id ? "walk_piece" : q.preset,
    template_id: q.template_id,
    title: q.title,
    description: q.description,
    cover_url: q.cover_url,
    tagline: q.tagline,
    city: q.city,
    country: q.country,
    tags: q.tags,
    story: q.story,
    manners: q.manners,
    quest_type: q.quest_type ?? null,
    start_fixed: q.start_fixed ?? false,
    time_limit_s: q.time_limit_s ?? null,
    route_settings_done: q.route_settings_done ?? false,
    center_lat: q.center_lat,
    center_lng: q.center_lng,
    page_order: q.page_order ?? null,
    page_photos: (q.page_photos ?? []).map((p) => ({ caption: p.caption ?? null })),
    page_blocks: (q.page_blocks ?? []).map(pageBlockWriteBody),
    spots: q.checkpoints.map((s, seq) => ({
      lat: s.lat,
      lng: s.lng,
      radius_m: s.radius_m,
      title: s.title,
      est_minutes: s.est_minutes,
      needs_media: s.needs_media,
      hidden_story: s.hidden_story ?? null,
      pose_hint: s.pose_hint ?? null,
      link_url: s.link_url ?? null,
      kind: s.kind,
      label: s.label,
      thumbnail_key: spotThumbnailKeyFromImageUrl(q.id, seq, s.thumbnail_image ?? null),
      images: normalizeQuestSpotImages(s.images).map((img) => ({ caption: img.caption ?? null })),
    })),
  };
}
